/**
 * Which accounts a surface shows (owner 2026-09-27: "an option to show just the main account or
 * all the accounts like Tradovate"). Viewing only: nothing here selects an account for orders.
 *
 * - The top bar holds the global **selection**: "all" or one account id.
 * - The user marks one **main** account (synced setting `main_account_id`); without one, the
 *   first connected account is main.
 * - Positions / Orders / Fills each carry a **filter**: follow the top bar ("global"), "all",
 *   "main", or one account (`account:<id>`), like Tradovate's per-module account filter.
 * - Per-account surfaces (DOM, order pad, risk chips) act on the **trading account**: the selected
 *   account, or the main one while "All accounts" is selected.
 */

/** "all" or an account id (plain string: account ids are arbitrary). */
export type AccountSelection = string;
export type AccountFilter = "global" | "all" | "main" | `account:${string}`;
export type AccountScope =
  { kind: "all" } | { kind: "one"; id: string } | { kind: "none"; missing: string | null };
/** The Accounts module's toggle. */
export type AccountsViewMode = "main" | "all";

export const ALL_ACCOUNTS = "all";

/** The main account: the setting when that account is connected, else the first account. */
export function mainAccountOf(mainSetting: string | null, ids: readonly string[]): string | null {
  if (mainSetting !== null && ids.includes(mainSetting)) return mainSetting;
  return ids[0] ?? null;
}

/** The account per-account surfaces act on: the selected one, else main. */
export function tradingAccountOf(
  selection: AccountSelection | null,
  mainSetting: string | null,
  ids: readonly string[],
): string | null {
  if (selection !== null && selection !== ALL_ACCOUNTS && ids.includes(selection)) return selection;
  return mainAccountOf(mainSetting, ids);
}

/** The top bar's effective selection: "all", a connected account, or null with no accounts. */
export function effectiveSelection(
  selection: AccountSelection | null,
  mainSetting: string | null,
  ids: readonly string[],
): AccountSelection | null {
  if (ids.length === 0) return null;
  if (selection === ALL_ACCOUNTS) return ALL_ACCOUNTS;
  return tradingAccountOf(selection, mainSetting, ids);
}

/**
 * The account an order-entry surface (DOM ladder, order pad, chart order bar) sends to — unchanged
 * routing, made testable (owner 2026-10-04): the selection its account picker shows. Every surface's
 * picker reads and writes the same selection as the top bar (`AccountSelect`), so a new panel starts
 * on the status-line account.
 *
 * - `account`: the selection as the gates see it ("all", an account id, or null without accounts);
 * - `accountId`: the account orders go to, or null while "All accounts" (view-only for orders) or no
 *   account is selected — the gate then refuses entry and says why.
 */
export function entryAccountOf(selection: AccountSelection | null): {
  account: AccountSelection | null;
  accountId: string | null;
} {
  if (selection === null) return { account: null, accountId: null };
  if (selection === ALL_ACCOUNTS) return { account: ALL_ACCOUNTS, accountId: null };
  return { account: selection, accountId: selection };
}

export function accountFilterOf(id: string): AccountFilter {
  return `account:${id}`;
}

/** Read a stored filter; anything unknown follows the top bar. */
export function parseAccountFilter(raw: unknown): AccountFilter {
  if (raw === "global" || raw === "all" || raw === "main") return raw;
  if (typeof raw === "string" && raw.startsWith("account:") && raw.length > "account:".length)
    return raw as AccountFilter;
  return "global";
}

export function parseAccountsViewMode(raw: unknown): AccountsViewMode {
  return raw === "main" ? "main" : "all";
}

/** Resolve a module's filter against the top-bar selection and the connected accounts. */
export function resolveAccountScope(
  filter: AccountFilter,
  selection: AccountSelection | null,
  mainSetting: string | null,
  ids: readonly string[],
): AccountScope {
  if (ids.length === 0) return { kind: "none", missing: null };
  switch (filter) {
    case "all":
      return { kind: "all" };
    case "main": {
      const id = mainAccountOf(mainSetting, ids);
      return id === null ? { kind: "none", missing: null } : { kind: "one", id };
    }
    case "global": {
      const sel = effectiveSelection(selection, mainSetting, ids);
      if (sel === ALL_ACCOUNTS) return { kind: "all" };
      return sel === null ? { kind: "none", missing: null } : { kind: "one", id: sel };
    }
    default: {
      const id = filter.slice("account:".length);
      return ids.includes(id) ? { kind: "one", id } : { kind: "none", missing: id };
    }
  }
}

export function inAccountScope(scope: AccountScope, accountId: string): boolean {
  return scope.kind === "all" || (scope.kind === "one" && scope.id === accountId);
}

export function filterByAccountScope<T extends { accountId: string }>(
  rows: readonly T[],
  scope: AccountScope,
): T[] {
  return rows.filter((r) => inAccountScope(scope, r.accountId));
}

/** Rows show an Account column whenever more than one account can appear. */
export function showsAccountColumn(scope: AccountScope, accountCount: number): boolean {
  return scope.kind === "all" && accountCount > 1;
}

/** The Accounts module: every account, or just the main one. */
export function accountsForView(
  mode: AccountsViewMode,
  mainSetting: string | null,
  ids: readonly string[],
): string[] {
  if (mode === "all") return [...ids];
  const main = mainAccountOf(mainSetting, ids);
  return main === null ? [] : [main];
}

/**
 * Rows grouped by account for the "All accounts" tables (owner decision 2026-09-27: per-account
 * subtotals like Tradovate). Groups follow `accountOrder` (the account list order), then any
 * account not in it in first-seen order; rows keep their order inside a group.
 */
export function groupByAccount<T extends { accountId: string }>(
  rows: readonly T[],
  accountOrder: readonly string[],
): { accountId: string; rows: T[] }[] {
  const byId = new Map<string, T[]>();
  for (const r of rows) {
    const g = byId.get(r.accountId);
    if (g) g.push(r);
    else byId.set(r.accountId, [r]);
  }
  const order = [
    ...accountOrder.filter((id) => byId.has(id)),
    ...[...byId.keys()].filter((id) => !accountOrder.includes(id)),
  ];
  return order.map((accountId) => ({ accountId, rows: byId.get(accountId) ?? [] }));
}

/** Σ of nullable cents; null when any value is unknown or there is nothing to add. */
export function sumCents(values: readonly (bigint | null)[]): bigint | null {
  if (values.length === 0) return null;
  let t = 0n;
  for (const v of values) {
    if (v === null) return null;
    t += v;
  }
  return t;
}
